import { DEFAULT_ARRANGEMENT, Lang, PAISE_PER_RUPEE, Trade } from "@gigsync/protocol";
import { asciiDigits } from "../job/details.js";
import { readSkill, spokenSkill } from "../skills/read.js";
import { parseAmount } from "./amount.js";
import { advance, askable, edit, FIELDS, Pick, retry, Slot } from "./card.js";
import { chooseArrangements, hearArrangements, hearMoreRate, mainOf } from "./card-arrangements.js";
import { cardEstimate, suggestedAmount } from "./card-estimate.js";
import { areaIn, editIntent, fieldIn, isNone, isSkip, languagesIn, tradesIn, yesNo } from "./lexicon.js";
/**
 * Turning an answer, said, typed or tapped, into the interview's next state: each field's checks, the amount check and
 * readback, and the line about the work, which must not carry anything private onto the public board.
 */
/** Two unclear answers to a yes-or-no question count as no: nothing is agreed to by default. */
const UNCLEAR_LIMIT = 2;
/** An amount this far outside the city's usual range for the trade is checked before it is kept. */
const BAND_SPREAD = 4;
const NO_BAND_LOW = 50 * PAISE_PER_RUPEE;
const NO_BAND_HIGH = 50_000 * PAISE_PER_RUPEE;
const Say = {
    againAmount: "iv.again.amount",
    againYesNo: "iv.again.yes_no",
};
/** Why a line about the work cannot go on the public card. */
const AboutProblem = { private: "private", long: "long" };
/** What the board keeps of the line about their work. */
const ABOUT_MAX = 300;
/** Ten digits or more, spaced or not: a phone number. */
const PHONE = /(?:\d[\s-]?){9}\d/;
/** Anything shaped like a UPI ID or an email address. */
const HANDLE = /\S@\S/;
/**
 * A line about the work is public, so a phone number or UPI ID in it would bypass the rule that contact is released
 * only after both sides sign. The interview checks the words as said; publishing checks them again, for the My card
 * form too.
 */
export function aboutIsPrivate(text) {
    return PHONE.test(asciiDigits(text)) || HANDLE.test(text);
}
function aboutProblem(text) {
    if (aboutIsPrivate(text))
        return AboutProblem.private;
    return text.trim().length > ABOUT_MAX ? AboutProblem.long : undefined;
}
function onTrades(s, trades) {
    if (trades.length === 0)
        return retry(s, { key: "iv.again.trade" });
    const keep = askable(trades);
    const ask_me = (s.draft.ask_me ?? []).filter((k) => keep.includes(k));
    const { skills: _replaced, ...rest } = s.draft;
    return advance(s, { ...rest, trades, ask_me });
}
/** The work, said: the trades it names, else the worker's own skill in a few words (ADR 0047), else ask again. */
function onWork(s, text) {
    const trades = tradesIn(text);
    if (trades.length > 0)
        return onTrades(s, trades);
    const read = readSkill(spokenSkill(text), s.config.own);
    if ("trade" in read)
        return onTrades(s, [read.trade]);
    if ("problem" in read)
        return retry(s, { key: `skill.err.${read.problem}` });
    const draft = { ...s.draft, trades: [], skills: [read.skill], ask_me: [] };
    return advance(s, draft, [{ key: "iv.skill_kept", params: { skill: read.skill.text } }]);
}
function onArea(s, area) {
    return area === undefined ? retry(s, { key: "iv.again.area" }) : advance(s, { ...s.draft, area });
}
/**
 * Whether an amount sits within the usual range for the worker's first trade (the estimate's, else the city band's),
 * or a wide one when there is neither.
 */
function plausible(s, paise) {
    // The city band is monthly: a rate for an hour, a visit or a week is checked against its estimate alone.
    const monthly = (mainOf(s.draft) ?? DEFAULT_ARRANGEMENT) === DEFAULT_ARRANGEMENT;
    const cityBand = monthly ? s.config.rates.bands.find((b) => b.trade === s.draft.trades?.[0]) : undefined;
    const band = cardEstimate(s)?.estimate ?? cityBand;
    const low = band ? band.low_paise / BAND_SPREAD : NO_BAND_LOW;
    const high = band ? band.high_paise * BAND_SPREAD : NO_BAND_HIGH;
    return paise >= low && paise <= high;
}
/**
 * Whether a line carrying the lowest rate may be read aloud: only when the owner said the amount aloud, so everyone
 * in earshot heard it already. Typed, it was meant to stay private, and it is shown but never spoken.
 */
export const minAloud = (aloud) => (aloud ? {} : { silent: true });
function keepAmount(s, paise, aloud) {
    if (s.slot === Slot.rate)
        return advance(s, { ...s.draft, rate_paise: paise }, [{ key: "iv.got_rate", prices: { price: paise } }]);
    const readback = { key: "iv.got_min", prices: { price: paise }, ...minAloud(aloud) };
    return advance(s, { ...s.draft, limit_paise: paise }, [readback]);
}
function onAmount(s, paise, aloud) {
    const cleared = { ...s, pending: undefined, pendingAloud: undefined };
    if (paise === undefined || paise <= 0)
        return retry(cleared, { key: Say.againAmount });
    const rate = s.draft.rate_paise;
    if (s.slot === Slot.min && rate !== undefined && paise > rate)
        return retry(cleared, { key: "iv.err_min", prices: { price: rate } });
    if (!plausible(s, paise))
        return { ...cleared, pending: paise, pendingAloud: aloud, notes: [], fresh: false };
    return keepAmount(cleared, paise, aloud);
}
/**
 * The answer to "Did you mean ₹…?": yes or the same amount keeps it; another amount is checked afresh. A yes keeps the
 * amount as it was given: typed stays quiet even when the yes is spoken.
 */
function onCheck(s, yes, paise, aloud) {
    const pending = s.pending ?? 0;
    const cleared = { ...s, pending: undefined, pendingAloud: undefined };
    if (yes === true || paise === pending)
        return keepAmount(cleared, pending, s.pendingAloud === true || (paise === pending && aloud));
    if (paise !== undefined)
        return onAmount(s, paise, aloud);
    if (yes === false)
        return retry(cleared, { key: Say.againAmount });
    return retry(s, { key: Say.againYesNo });
}
function onLanguages(s, named) {
    if (named === undefined && s.misses + 1 < UNCLEAR_LIMIT)
        return { ...retry(s, { key: "iv.again.languages" }), misses: s.misses + 1 };
    // The worker's own language always comes first: their card's free text is written in it.
    const own = s.config.own;
    return advance(s, { ...s.draft, languages: [own, ...(named ?? []).filter((l) => l !== own)] });
}
function onAbout(s, text) {
    const { about: _old, ...rest } = s.draft;
    if (text === undefined)
        return advance(s, rest);
    const problem = aboutProblem(text);
    if (problem === AboutProblem.private)
        return retry(s, { key: "iv.err_private" });
    if (problem === AboutProblem.long)
        return retry(s, { key: "iv.err_long" });
    return advance(s, { ...rest, about: text.trim() });
}
function onYesNo(s, yes) {
    const value = yes ?? (s.misses + 1 >= UNCLEAR_LIMIT ? false : undefined);
    if (value === undefined)
        return { ...retry(s, { key: Say.againYesNo }), misses: s.misses + 1 };
    if (s.slot === Slot.auto)
        return advance(s, { ...s.draft, auto: value });
    const keys = askable(s.draft.trades ?? []);
    const key = keys[s.ask];
    const chosen = new Set(s.draft.ask_me);
    if (key && value)
        chosen.add(key);
    if (key && !value)
        chosen.delete(key);
    const draft = { ...s.draft, ask_me: keys.filter((k) => chosen.has(k)) };
    if (s.ask + 1 < keys.length)
        return { ...s, draft, ask: s.ask + 1, misses: 0, notes: [], fresh: false };
    return advance(s, draft);
}
function onConfirm(s, yes, field) {
    if (field)
        return edit(s, field);
    if (yes === true)
        return { ...s, slot: Slot.done, notes: [], fresh: false };
    if (yes === false)
        return { ...s, changing: true, notes: [], fresh: false };
    return retry(s, { key: Say.againYesNo });
}
/** Which field to change; a no or "nothing" goes back to the summary and its question. */
function onChange(s, field, yes) {
    if (field)
        return edit(s, field);
    return { ...s, changing: yes !== false, notes: [], fresh: false };
}
/** Languages named, none (an empty list), or undefined when the answer is neither. */
function heardLanguages(text) {
    const named = languagesIn(text);
    if (named.length > 0)
        return named;
    return isNone(text) || yesNo(text) === false ? [] : undefined;
}
/**
 * An answer in words: `aloud` when the owner said it, false when they typed it. It decides only whether the lowest
 * rate is read back aloud; leaving it out keeps that readback quiet.
 */
export function hear(state, text, aloud = false) {
    switch (state.slot) {
        case Slot.trade:
            return onWork(state, text);
        case Slot.arrangements:
            return hearArrangements(state, text);
        case Slot.more_rates:
            return hearMoreRate(state, text);
        case Slot.area:
            return onArea(state, areaIn(text, state.config.areas));
        case Slot.rate:
        case Slot.min:
            return state.pending === undefined
                ? onAmount(state, parseAmount(text), aloud)
                : onCheck(state, yesNo(text), parseAmount(text), aloud);
        case Slot.languages:
            return onLanguages(state, heardLanguages(text));
        case Slot.about:
            return onAbout(state, isSkip(text) ? undefined : text);
        case Slot.ask_first:
        case Slot.auto:
            return onYesNo(state, yesNo(text));
        case Slot.confirm:
            return state.changing
                ? onChange(state, fieldIn(text), yesNo(text))
                : onConfirm(state, yesNo(text), editIntent(text));
        default:
            return state;
    }
}
const flag = (value) => value === Pick.yes ? true : value === Pick.no ? false : undefined;
/** An answer by tapping one of the prompt's choices. A value the prompt did not offer changes nothing. */
export function choose(state, value) {
    switch (state.slot) {
        case Slot.trade: {
            const trade = Trade.options.find((tr) => tr === value);
            return trade ? onTrades(state, [trade]) : state;
        }
        case Slot.arrangements:
            return chooseArrangements(state, value);
        case Slot.area:
            return state.config.areas.some((a) => a.key === value) ? onArea(state, value) : state;
        case Slot.rate:
        case Slot.min: {
            if (state.pending !== undefined)
                return flag(value) === undefined ? state : onCheck(state, flag(value), undefined, false);
            const suggested = value === Pick.suggested ? suggestedAmount(state, state.slot === Slot.min) : undefined;
            return suggested === undefined ? state : onAmount(state, suggested, false);
        }
        case Slot.languages: {
            const lang = Lang.options.find((l) => l === value);
            return lang || value === Pick.none ? onLanguages(state, lang ? [lang] : []) : state;
        }
        case Slot.about:
            return value === Pick.skip ? onAbout(state, undefined) : state;
        case Slot.ask_first:
        case Slot.auto:
            return flag(value) === undefined ? state : onYesNo(state, flag(value));
        case Slot.confirm: {
            if (!state.changing)
                return flag(value) === undefined ? state : onConfirm(state, flag(value), undefined);
            const field = FIELDS.find((f) => f === value);
            return field ? edit(state, field) : state;
        }
        default:
            return state;
    }
}
//# sourceMappingURL=card-parse.js.map